import { useEffect, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';

import { AuthLayout } from '@/modules/auth/components/AuthLayout';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { getAuthenticatedDestination } from '@/modules/auth/lib/profileCompletion';
import {
  createCompleteProfileSchema,
  type CompleteProfileFormValues,
} from '@/modules/auth/schemas/complete-profile.schemas';
import { isApiError } from '@/shared/lib/apiClient';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';

export const Route = createFileRoute('/complete-profile')({
  component: CompleteProfilePage,
});

function CompleteProfilePage() {
  const { t } = useTranslation();
  const { user, isAuthLoading, completeProfile, logout } = useAuth();
  const navigate = useNavigate();

  const requireCompanyName = user?.missingFields.includes('companyName') ?? false;
  const schema = useMemo(
    () => createCompleteProfileSchema(requireCompanyName),
    [requireCompanyName],
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<CompleteProfileFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      fullName: '',
      companyName: '',
    },
  });

  useEffect(() => {
    if (!isAuthLoading && !user) {
      void navigate({ to: '/auth/login', replace: true });
      return;
    }
    if (!isAuthLoading && user && !user.requiresProfileCompletion) {
      void navigate({ to: getAuthenticatedDestination(user), replace: true });
    }
  }, [user, isAuthLoading, navigate]);

  const onSubmit = async (values: CompleteProfileFormValues) => {
    try {
      const updated = await completeProfile({
        fullName: values.fullName.trim(),
        ...(values.companyName?.trim() ? { companyName: values.companyName.trim() } : {}),
      });
      await navigate({ to: getAuthenticatedDestination(updated), replace: true });
    } catch (err) {
      let message = t('completeProfile.errors.generic');
      if (isApiError(err)) {
        if (err.code === 'FULL_NAME_TEMPORARY') {
          message = t('completeProfile.errors.temporaryName');
        } else if (err.code === 'COMPANY_NAME_REQUIRED') {
          message = t('completeProfile.errors.companyRequired');
        } else if (err.status === 400) {
          message = t('completeProfile.errors.validation');
        }
      }
      setError('root', { message });
    }
  };

  if (isAuthLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user.requiresProfileCompletion) {
    return null;
  }

  return (
    <AuthLayout>
      <Card>
        <CardHeader>
          <CardTitle>{t('completeProfile.title')}</CardTitle>
          <CardDescription>{t('completeProfile.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            {errors.root && (
              <Alert variant="destructive">
                <AlertDescription>{errors.root.message}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="fullName">{t('completeProfile.fullName')}</Label>
              <Input
                id="fullName"
                autoComplete="name"
                aria-invalid={Boolean(errors.fullName)}
                {...register('fullName')}
              />
              {errors.fullName && (
                <p className="text-sm text-destructive">{t(errors.fullName.message ?? '')}</p>
              )}
            </div>

            {requireCompanyName && (
              <div className="space-y-1.5">
                <Label htmlFor="companyName">{t('completeProfile.companyName')}</Label>
                <Input
                  id="companyName"
                  autoComplete="organization"
                  aria-invalid={Boolean(errors.companyName)}
                  {...register('companyName')}
                />
                {errors.companyName && (
                  <p className="text-sm text-destructive">{t(errors.companyName.message ?? '')}</p>
                )}
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" className="flex-1" isLoading={isSubmitting}>
                {isSubmitting ? t('completeProfile.saving') : t('completeProfile.save')}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => void logout().then(() => navigate({ to: '/auth/login' }))}
              >
                {t('completeProfile.logout')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
